import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Users, Calendar, ShieldCheck, Eye, Heart, Layers,
  PlusSquare, Megaphone, ClipboardCheck, MapPinned, BarChart3,
  FileCheck2, BadgeCheck, ArrowRight, Bell, Landmark, TrendingUp,
  TrendingDown, Minus, Clock, MapPin, Phone, List, LayoutGrid,
  User, CheckCircle, MessageSquare, Image as ImageIcon, RefreshCw
} from "lucide-react";
import { api, asItems, formatApiError, formatPrice, prefixUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";

const ROLE_COPY = {
  buyer: {
    eyebrow: "Buyer dashboard",
    title: "Find, compare and visit published properties",
    description: "Manage saved properties, contact history, site visits, and Plots on Demand requirements.",
  },
  owner: {
    eyebrow: "Owner dashboard",
    title: "List property and track buyer interest",
    description: "Add property details, upload images/documents, track verification and leads.",
  },
  agent: {
    eyebrow: "Agent dashboard",
    title: "Review leads and coordinate site visits",
    description: "Manage assigned leads, coordinate with offices, and schedule property walk-throughs.",
  },
  office: {
    eyebrow: "Office dashboard",
    title: "Oversee agent leads and property verifications",
    description: "Review uploaded papers, moderate listings, assign leads, and monitor visits.",
  },
  admin: {
    eyebrow: "Admin dashboard",
    title: "Full system administrative control panel",
    description: "Monitor analytical metrics, manage registrations, approve agents/offices, and audit logs.",
  },
  super_admin: {
    eyebrow: "Super admin control plane",
    title: "Platform security and governance",
    description: "Oversee platform settings, audit controls, users, roles and operational metrics.",
  },
};

const formatDateTime = (dtStr) => {
  if (!dtStr) return "—";
  try {
    const d = new Date(dtStr);
    return d.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) + " · " + d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dtStr;
  }
};

const getImageUrl = (urlObj) => {
  if (!urlObj) return "";
  const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
  if (!url) return "";
  return prefixUrl(url);
};

// Human-friendly labels so admins immediately understand what each lifecycle
// state means (instead of raw enum values like PENDING_SOLD_VERIFICATION).
const STATUS_LABELS = {
  // Property lifecycle
  DRAFT: "Draft",
  PENDING_VERIFICATION: "Awaiting Verification",
  IN_PROCESS: "In Review",
  VERIFIED: "Office Verified",
  PUBLISHED: "Live",
  BOOKED: "Booked",
  PENDING_SOLD_VERIFICATION: "Sale Pending Approval",
  SOLD: "Sold",
  REJECTED: "Rejected",
  ARCHIVED: "Archived",
  // Lead pipeline
  NEW: "New",
  CONTACTED: "Contacted",
  INTERESTED: "Interested",
  SITE_VISIT_SCHEDULED: "Visit Scheduled",
  NEGOTIATION: "Negotiating",
  LOST: "Lost",
  // Plot demand
  ASSIGNED: "Assigned",
  MATCHING: "Matching Properties",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  // User / office
  ACTIVE: "Active",
  PENDING: "Pending Approval",
  BLOCKED: "Blocked",
};
const prettyStatus = (s) =>
  STATUS_LABELS[String(s || "").toUpperCase()] ||
  String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [upcomingVisit, setUpcomingVisit] = useState(null);
  const [visitError, setVisitError] = useState("");
  const [recentProps, setRecentProps] = useState([]);
  const [recentPropsError, setRecentPropsError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const role = String(user?.role || "buyer").toLowerCase().replace("office_user", "office");
  const copy = ROLE_COPY[role] || ROLE_COPY.buyer;

  useEffect(() => {
    let live = true;
    setLoading(true);
    setStatsError("");
    api.get("/dashboard/stats")
      .then(({ data }) => { if (live) setStats(data || {}); })
      .catch((error) => {
        if (live) {
          setStats({});
          setStatsError(formatApiError(error));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [reloadKey]);

  // Real recent property records for the admin overview table (replaces
  // the old hardcoded placeholder rows). Each row links to the property page.
  useEffect(() => {
    if (role !== "admin" && role !== "super_admin") return;
    let live = true;
    setRecentPropsError("");
    api.get("/admin/properties", { params: { limit: 6, sort: "newest" } })
      .then(({ data }) => { if (live) setRecentProps(asItems(data).slice(0, 6)); })
      .catch((error) => {
        if (live) {
          setRecentProps([]);
          setRecentPropsError(formatApiError(error));
        }
      });
    return () => { live = false; };
  }, [role, reloadKey]);

  useEffect(() => {
    if (role === "buyer" || role === "owner" || role === "agent") {
      let live = true;
      setVisitError("");
      setUpcomingVisit(null);
      // Owners may also book properties as buyers. This personal queue remains
      // separate from the listing-generated visit total shown in their KPI.
      const endpoint = role === "agent" ? "/v1/agent/site-visits/me" : "/v1/site-visits";
      api.get(endpoint)
        .then(({ data }) => {
          const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
          const activeVisits = list.filter(v => {
            const s = String(v.status || "").toUpperCase();
            return s === "CONFIRMED" || s === "SCHEDULED" || s === "IN_PROGRESS" || s === "PENDING" || s === "REQUESTED";
          });
          activeVisits.sort((a, b) => {
            const da = a.scheduled_at || a.visit_at || a.date;
            const db = b.scheduled_at || b.visit_at || b.date;
            if (!da) return 1;
            if (!db) return -1;
            return new Date(da) - new Date(db);
          });
          if (live && activeVisits.length > 0) {
            setUpcomingVisit(activeVisits[0]);
          }
        })
        .catch((error) => { if (live) setVisitError(formatApiError(error)); });
      return () => { live = false; };
    }
    return undefined;
  }, [role, reloadKey]);

  const kpis = useMemo(() => kpisFor(role, stats), [role, stats]);
  const actions = useMemo(() => actionsFor(role), [role]);
  const pipeline = normalizePipeline(stats.properties || stats.property_status || []);

  if (loading) {
    return (
      <div className="min-h-[320px] rounded-3xl border border-border bg-background p-8 grid place-items-center" role="status" aria-live="polite" data-testid="dashboard-loading">
        <div className="text-center">
          <RefreshCw className="mx-auto h-7 w-7 animate-spin text-primary" aria-hidden="true" />
          <p className="mt-3 font-semibold text-foreground">Loading dashboard…</p>
          <p className="mt-1 text-sm text-muted-foreground">Fetching the latest records for your account.</p>
        </div>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="min-h-[320px] rounded-3xl border border-destructive/30 bg-destructive/5 p-6 sm:p-8 grid place-items-center" role="alert" data-testid="dashboard-error">
        <div className="max-w-lg text-center">
          <h1 className="font-display text-2xl font-black text-foreground">Dashboard could not be loaded</h1>
          <p className="mt-2 text-sm text-muted-foreground">{statsError}</p>
          <Button type="button" className="mt-5 gap-2" onClick={() => setReloadKey((value) => value + 1)} title="Retry loading dashboard">
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  if (role === "admin" || role === "super_admin") {
    const renderBadge = (pctChange, direction) => {
      if (direction === "up") {
        return (
          <div className="flex items-center gap-1 text-emerald-400 font-mono text-xs bg-emerald-400/10 px-2 py-0.5 rounded">
            <TrendingUp className="w-3 h-3" />
            <span>+{pctChange}%</span>
          </div>
        );
      }
      if (direction === "down") {
        return (
          <div className="flex items-center gap-1 text-red-400 font-mono text-xs bg-red-400/10 px-2 py-0.5 rounded">
            <TrendingDown className="w-3 h-3" />
            <span>{pctChange}%</span>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-1 text-muted-foreground font-mono text-[10px] bg-muted/50 px-2 py-0.5 rounded uppercase font-semibold">
          <Minus className="w-3.5 h-3.5 inline mr-0.5" />
          <span>CURRENT</span>
        </div>
      );
    };

    const STATUS_COLORS = {
      // Property lifecycle
      DRAFT: { bar: "bg-zinc-400", text: "text-zinc-400" },
      PENDING_VERIFICATION: { bar: "bg-amber-500", text: "text-amber-500" },
      IN_PROCESS: { bar: "bg-sky-500", text: "text-sky-500" },
      VERIFIED: { bar: "bg-blue-500", text: "text-blue-500" },
      PUBLISHED: { bar: "bg-emerald-500", text: "text-emerald-500" },
      BOOKED: { bar: "bg-cyan-500", text: "text-cyan-500" },
      PENDING_SOLD_VERIFICATION: { bar: "bg-orange-500", text: "text-orange-500" },
      SOLD: { bar: "bg-violet-500", text: "text-violet-500" },
      REJECTED: { bar: "bg-rose-500", text: "text-rose-500" },
      ARCHIVED: { bar: "bg-zinc-500", text: "text-zinc-500" },
      // Lead pipeline
      NEW: { bar: "bg-sky-500", text: "text-sky-500" },
      CONTACTED: { bar: "bg-amber-500", text: "text-amber-500" },
      INTERESTED: { bar: "bg-purple-500", text: "text-purple-500" },
      SITE_VISIT_SCHEDULED: { bar: "bg-indigo-500", text: "text-indigo-500" },
      NEGOTIATION: { bar: "bg-orange-500", text: "text-orange-500" },
      LOST: { bar: "bg-rose-500", text: "text-rose-500" },
      IN_PROGRESS: { bar: "bg-blue-500", text: "text-blue-500" },
      CONVERTED: { bar: "bg-emerald-500", text: "text-emerald-500" },
      // Plot demand
      ASSIGNED: { bar: "bg-blue-500", text: "text-blue-500" },
      MATCHING: { bar: "bg-amber-500", text: "text-amber-500" },
      COMPLETED: { bar: "bg-emerald-500", text: "text-emerald-500" },
      CANCELLED: { bar: "bg-rose-500", text: "text-rose-500" },
      // User / office
      ACTIVE: { bar: "bg-emerald-500", text: "text-emerald-500" },
      PENDING: { bar: "bg-amber-500", text: "text-amber-500" },
      BLOCKED: { bar: "bg-rose-500", text: "text-rose-500" },
    };

    const getStatusTheme = (status) => {
      return STATUS_COLORS[String(status).toUpperCase()] || { bar: "bg-primary", text: "text-primary" };
    };

    const allPropStatuses = { DRAFT: 0, PENDING_VERIFICATION: 0, IN_PROCESS: 0, VERIFIED: 0, PUBLISHED: 0, BOOKED: 0, PENDING_SOLD_VERIFICATION: 0, SOLD: 0, REJECTED: 0, ARCHIVED: 0 };
    const propStats = Object.assign({}, allPropStatuses, countMap(stats.properties || []));
    const totalProps = total(propStats);
    const propPctChange = stats.prop_pct_change ? String(stats.prop_pct_change) : "0.0";
    const propDir = "stable";

    const usersByRole = stats.users_by_role || [];
    const getRoleStatusCounts = (roleName) => {
      const list = usersByRole.filter(u => String(u.role || "").toUpperCase() === roleName.toUpperCase());
      const counts = {};
      list.forEach(u => {
        counts[String(u.status || "PENDING").toUpperCase()] = Number(u.count || 0);
      });
      return counts;
    };

    const allBuyerStatuses = { ACTIVE: 0, PENDING: 0, BLOCKED: 0 };
    const buyerStats = Object.assign({}, allBuyerStatuses, getRoleStatusCounts("BUYER"));
    const totalBuyers = total(buyerStats);
    const buyerPctChange = stats.buyer_pct_change ? String(stats.buyer_pct_change) : "0.0";
    const buyerDir = "stable";

    const allLeadStatuses = { NEW: 0, CONTACTED: 0, INTERESTED: 0, SITE_VISIT_SCHEDULED: 0, NEGOTIATION: 0, BOOKED: 0, SOLD: 0, LOST: 0 };
    const leadStats = Object.assign({}, allLeadStatuses, countMap(stats.leads || []));
    const totalLeads = total(leadStats);
    const leadPctChange = stats.lead_pct_change ? String(stats.lead_pct_change) : "0.0";
    const leadDir = "stable";

    const allAgentStatuses = { ACTIVE: 0, PENDING: 0, BLOCKED: 0 };
    const agentStats = Object.assign({}, allAgentStatuses, getRoleStatusCounts("AGENT"));
    const totalAgents = total(agentStats);
    const agentPctChange = stats.agent_pct_change ? String(stats.agent_pct_change) : "0.0";
    const agentDir = "stable";

    const allOfficeStatuses = { ACTIVE: 0, PENDING: 0 };
    const officeStats = Object.assign({}, allOfficeStatuses, countMap(stats.offices || []));
    const totalOffices = total(officeStats);

    const allPlotStatuses = { NEW: 0, MATCHING: 0, ASSIGNED: 0, COMPLETED: 0, CANCELLED: 0 };
    const plotStats = Object.assign({}, allPlotStatuses, countMap(stats.plot_demands || []));
    const totalPlots = total(plotStats);
    const plotPctChange = stats.plot_pct_change ? String(stats.plot_pct_change) : "0.0";
    const plotDir = "stable";

    const renderStatusBreakdown = (statsMap, totalCount) => {
      if (totalCount === 0) return <p className="text-[11px] text-muted-foreground italic">No data available</p>;
      // Only show states that actually have items — empty states convey nothing
      // and just clutter the card, so they're hidden until they hold data.
      const entries = Object.entries(statsMap).filter(([, count]) => Number(count) > 0);
      const isMulti = entries.length > 3;
      return (
        <div className={isMulti ? "grid grid-cols-2 gap-x-4 gap-y-3 mt-4" : "space-y-3 mt-4"}>
          {entries.map(([status, count]) => {
            const theme = getStatusTheme(status);
            const pct = totalCount > 0 ? (count / totalCount) * 100 : 0;
            return (
              <div key={status} className="group/item min-w-0">
                <div className="flex justify-between text-[10px] font-mono mb-1">
                  <span className="text-muted-foreground flex items-center gap-1 uppercase tracking-wider truncate mr-1">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${theme.bar}`} />
                    <span className="truncate">{prettyStatus(status)}</span>
                  </span>
                  <span className="text-foreground font-semibold font-mono shrink-0">{count.toLocaleString("en-IN")}</span>
                </div>
                <div className="h-1.5 w-full bg-muted/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${theme.bar} rounded-full animate-bar-grow`}
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="bg-background text-foreground min-h-screen animate-fade-up" data-testid="dashboard-overview">
        {/* Header */}
        <div className="bg-gradient-to-r from-card to-card/50 border border-border rounded-2xl p-6 mb-8 shadow-sm relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-slate-900/[0.04] bg-[size:20px_20px]" />
          <div className="absolute -top-12 -right-12 w-44 h-44 bg-primary/10 rounded-full blur-3xl pointer-events-none animate-float-soft" />
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-primary/70 mb-1.5">Control center</p>
            {/* Brand gradient accent on the section title */}
            <h2 className="font-display text-3xl font-semibold text-foreground"><span className="brand-gradient-text">Overview</span></h2>
            <p className="text-xs text-muted-foreground mt-1.5">Current platform records across every lifecycle stage.</p>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Card 1: Properties */}
          <Link to="/dashboard/admin/properties" className="block group animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
            <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 flex items-center justify-center text-primary shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-primary/20 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
                    <Building2 className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">Properties</span>
                </div>
                {renderBadge(propPctChange, propDir)}
              </div>
              <div className="mb-6">
                <div className="font-display text-3xl font-semibold text-foreground">{totalProps.toLocaleString("en-IN")}</div>
                <p className="text-muted-foreground text-[11px]">Total property records</p>
              </div>
              {renderStatusBreakdown(propStats, totalProps)}
            </div>
          </Link>

          {/* Card 2: Buyers */}
          <Link to="/dashboard/admin/users?role=buyer" className="block group animate-fade-up" style={{ animationDelay: '150ms', animationFillMode: 'both' }}>
            <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 flex items-center justify-center text-primary shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-primary/20 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
                    <Users className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">Buyers</span>
                </div>
                {renderBadge(buyerPctChange, buyerDir)}
              </div>
              <div className="mb-6">
                <div className="font-display text-3xl font-semibold text-foreground">{totalBuyers.toLocaleString("en-IN")}</div>
                <p className="text-muted-foreground text-[11px]">Registered buyer accounts</p>
              </div>
              {renderStatusBreakdown(buyerStats, totalBuyers)}
            </div>
          </Link>

          {/* Card 3: Leads */}
          <Link to="/dashboard/admin/leads" className="block group animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
            <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 flex items-center justify-center text-primary shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-primary/20 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
                    <BarChart3 className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">Leads</span>
                </div>
                {renderBadge(leadPctChange, leadDir)}
              </div>
              <div className="mb-6">
                <div className="font-display text-3xl font-semibold text-foreground">{totalLeads.toLocaleString("en-IN")}</div>
                <p className="text-muted-foreground text-[11px]">Leads across lifecycle statuses</p>
              </div>
              {renderStatusBreakdown(leadStats, totalLeads)}
            </div>
          </Link>

          {/* Card 4: Agents */}
          <Link to="/dashboard/admin/agents" className="block group animate-fade-up" style={{ animationDelay: '250ms', animationFillMode: 'both' }}>
            <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 flex items-center justify-center text-primary shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-primary/20 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
                    <BadgeCheck className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">Agents</span>
                </div>
                {renderBadge(agentPctChange, agentDir)}
              </div>
              <div className="mb-6">
                <div className="font-display text-3xl font-semibold text-foreground">{totalAgents.toLocaleString("en-IN")}</div>
                <p className="text-muted-foreground text-[11px]">Registered agent accounts</p>
              </div>
              {renderStatusBreakdown(agentStats, totalAgents)}
            </div>
          </Link>

          {/* Card 5: Offices */}
          <Link to="/dashboard/admin/offices" className="block group animate-fade-up" style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
            <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 flex items-center justify-center text-primary shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-primary/20 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
                    <Landmark className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">Offices</span>
                </div>
                {renderBadge("", "stable")}
              </div>
              <div className="mb-6">
                <div className="font-display text-3xl font-semibold text-foreground">{totalOffices.toLocaleString("en-IN")}</div>
                <p className="text-muted-foreground text-[11px]">Registered office records</p>
              </div>
              {renderStatusBreakdown(officeStats, totalOffices)}
            </div>
          </Link>

          {/* Card 6: buyer-raised Plots on Demand requests (not listed Plot/Land inventory). */}
          <Link to="/dashboard/admin/plots-on-demand" className="block group animate-fade-up" style={{ animationDelay: '350ms', animationFillMode: 'both' }}>
            <div className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 flex items-center justify-center text-primary shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:shadow-primary/20 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
                    <Layers className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">Plots on Demand</span>
                </div>
                {renderBadge(plotPctChange, plotDir)}
              </div>
              <div className="mb-6">
                <div className="font-display text-3xl font-semibold text-foreground">{totalPlots.toLocaleString("en-IN")}</div>
                <p className="text-muted-foreground text-[11px]">Buyer-raised land requirements</p>
              </div>
              {renderStatusBreakdown(plotStats, totalPlots)}
            </div>
          </Link>
        </div>

        {/* Bottom acquisitions table */}
        <div className="mt-10 animate-fade-up" style={{ animationDelay: '400ms', animationFillMode: 'both' }}>
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-border flex justify-between items-center bg-gradient-to-r from-muted/30 to-transparent">
              <h3 className="font-display font-semibold text-xl text-foreground">Recent Property Records</h3>
              <div className="flex items-center gap-3">
                <div className="flex bg-muted/60 p-0.5 rounded-lg border border-border/50">
                  <button 
                    type="button"
                    onClick={() => setViewMode("list")} 
                    className={`p-1.5 rounded-md transition-all ${viewMode === "list" ? "bg-background text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    title="List View"
                    aria-label="Show activity as a list"
                    aria-pressed={viewMode === "list"}
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button 
                    type="button"
                    onClick={() => setViewMode("grid")} 
                    className={`p-1.5 rounded-md transition-all ${viewMode === "grid" ? "bg-background text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    title="Grid View"
                    aria-label="Show activity as a grid"
                    aria-pressed={viewMode === "grid"}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>
                <Link to="/dashboard/admin/properties" className="text-primary font-mono text-xs font-bold hover:underline uppercase tracking-widest bg-primary/5 px-3 py-1.5 rounded-full">VIEW ALL</Link>
              </div>
            </div>
            
            {recentPropsError ? (
              <div className="px-6 py-12 text-center" role="alert">
                <p className="text-sm font-semibold text-foreground">Recent properties could not be loaded.</p>
                <p className="mt-1 text-sm text-muted-foreground">{recentPropsError}</p>
                <Button type="button" variant="outline" className="mt-4 gap-2" onClick={() => setReloadKey((value) => value + 1)}>
                  <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry
                </Button>
              </div>
            ) : viewMode === "grid" ? (
              recentProps.length === 0 ? (
                <div className="px-6 py-12 text-center text-muted-foreground text-sm">
                  No recent property records yet.
                </div>
              ) : (
                <div className="p-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {recentProps.map((p) => (
                    <PropertyCard key={p.property_id || p.id} property={p} />
                  ))}
                </div>
              )
            ) : (
              <div className="overflow-x-auto w-full scrollbar-none">
                <table className="w-full text-left border-collapse min-w-[700px]">
                  <thead>
                    <tr className="bg-muted/10 font-mono text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="px-6 py-4 font-semibold">Project</th>
                      <th className="px-6 py-4 font-semibold">Description</th>
                      <th className="px-6 py-4 font-semibold">Listed price</th>
                      <th className="px-6 py-4 font-semibold">Status</th>
                      <th className="px-6 py-4 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30 text-sm">
                    {recentProps.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center text-muted-foreground text-sm">
                          No recent property records yet.
                        </td>
                      </tr>
                    ) : recentProps.map((p, i) => {
                      const pid = p.property_id || p.id;
                      const theme = getStatusTheme(p.status);
                      const code = p.property_code || `#${String(pid || "").replace(/-/g, "").slice(0, 8).toUpperCase()}`;
                      const place = [p.locality, p.city].filter(Boolean).join(", ") || "—";
                      const imgUrl = p.images?.[0] || p.thumbnail_url || p.thumbnail;
                      return (
                        <tr key={pid || i} className="hover:bg-muted/10 transition-colors group animate-fade-up" style={{ animationDelay: `${i * 50}ms`, animationFillMode: "both" }}>
                          <td className="px-6 py-4">
                            <div className="w-12 h-12 rounded-md bg-secondary overflow-hidden shrink-0 border border-border/50 shadow-sm">
                              {imgUrl ? (
                                <img src={imgUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground font-mono text-[10px]">NA</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded tracking-wider shrink-0">{code}</span>
                              <Link to={`/properties/${pid}`} className="text-foreground group-hover:text-primary hover:underline font-medium line-clamp-1 transition-colors">
                                {p.title || "Untitled property"}
                              </Link>
                            </div>
                            <span className="block text-[11px] text-muted-foreground mt-1.5">{place}</span>
                          </td>
                          <td className="px-6 py-4 text-muted-foreground tabular-nums">{p.price ? formatPrice(p.price) : "—"}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 bg-muted/60 font-mono text-[10px] rounded ${theme.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${theme.bar}`} />
                              {p.status ? prettyStatus(p.status) : "—"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Link to={`/properties/${pid}`} className="text-muted-foreground hover:text-primary transition-colors inline-flex" title="View property">
                              <ArrowRight className="w-4 h-4 inline group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-overview">
      <div className="mb-6">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-muted-foreground">{copy.eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-primary">
          Welcome {user?.name || "User"}
        </h1>
        <p className="mt-2 max-w-3xl text-sm sm:text-base text-muted-foreground">{copy.description}</p>
        {user?.approved === false && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-4 py-1.5 text-sm font-bold text-amber-600 border border-amber-500/20">
            <ShieldCheck className="h-4 w-4" /> Account is pending admin approval
          </div>
        )}
      </div>

      {visitError && (
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between" role="alert">
          <div>
            <p className="text-sm font-bold text-foreground">Upcoming visits could not be loaded.</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{visitError}</p>
          </div>
          <Button type="button" size="sm" variant="outline" className="gap-2 self-start sm:self-auto" onClick={() => setReloadKey((value) => value + 1)}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry
          </Button>
        </div>
      )}

      {/* Upcoming Visit Reminder Alert Banner */}
      {upcomingVisit && (() => {
        const scheduledDate = upcomingVisit.scheduled_at || upcomingVisit.visit_at;
        let dateStr = "Date not scheduled";
        let timeStr = "Time not scheduled";
        if (scheduledDate) {
          try {
            const d = new Date(scheduledDate);
            dateStr = d.toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" });
            timeStr = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
          } catch (e) { /* keep honest unavailable labels */ }
        }
        
        const titleParts = (upcomingVisit.property?.title || "Property Listing").split(",");
        const mainTitle = titleParts[0].trim();
        
        // Location text
        const addressPart = upcomingVisit.property?.address ? upcomingVisit.property.address.trim() : "";
        const cityPart = upcomingVisit.property?.city ? upcomingVisit.property.city.trim() : "";
        const locationStr = addressPart && cityPart ? `${addressPart}, ${cityPart}` : (addressPart || cityPart || "Location unavailable");
        
        const imageCount = upcomingVisit.property?.images?.length || 0;
        
        return (
          <section 
            className="mt-6 rounded-[24px] border border-slate-800 bg-[#0a0e17] text-white shadow-2xl overflow-hidden flex flex-col md:flex-row items-stretch gap-0"
            data-testid="upcoming-visit-reminder"
          >
            {/* Left Content Column */}
            <div className="flex-1 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              
              {/* Property Image Container */}
              <div className="relative w-32 h-32 rounded-[20px] bg-slate-800 overflow-hidden shrink-0 border border-slate-700/60 shadow-inner">
                {upcomingVisit.property?.images?.[0] ? (
                  <img 
                    src={getImageUrl(upcomingVisit.property?.images?.[0])} 
                    alt="" 
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-500 bg-slate-900">No Photo</div>
                )}
                {/* Photo Count badge on image */}
                <div className="absolute bottom-2 left-2 bg-black/55 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1">
                  <ImageIcon className="w-3 h-3 text-white" />
                  <span>{imageCount}</span>
                </div>
              </div>

              {/* Text Info Middle Section */}
              <div className="flex-1 min-w-0 space-y-2">
                {/* Tag header */}
                <div className="inline-flex items-center gap-1 rounded-full border border-blue-500/30 px-3 py-1 text-[10px] font-bold text-blue-400 bg-blue-500/5">
                  <Calendar className="w-3 h-3" />
                  <span>UPCOMING VISIT</span>
                </div>

                {/* Title */}
                <h3 className="font-display font-bold text-2xl text-white leading-tight tracking-tight">
                  {mainTitle}
                </h3>

                {/* Meta details row */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-400 font-medium">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span>{dateStr}</span>
                  </span>
                  
                  <span className="text-slate-700">•</span>
                  
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span>{timeStr}</span>
                  </span>
                  
                  <span className="text-slate-700">•</span>
                  
                  <span className="flex items-center gap-1 truncate max-w-[280px]" title={locationStr}>
                    <MapPin className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="truncate">{locationStr}</span>
                  </span>
                </div>

                {/* Subtle Divider Line */}
                <div className="border-t border-slate-800/60 my-3 pt-3 flex flex-wrap items-center gap-4">
                  {/* Status Badging */}
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/5 border border-emerald-500/20 px-2.5 py-1 rounded-full shrink-0">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span>{prettyStatus(upcomingVisit.status)}</span>
                  </div>

                  <span className="h-4 w-px bg-slate-800 hidden sm:inline" />

                  {/* Agent name */}
                  <div className="flex items-center gap-1.5 text-xs text-slate-300 font-medium">
                    <User className="w-4 h-4 text-slate-400 shrink-0" />
                    <span>{upcomingVisit.agent_name || "Agent assignment pending"}</span>
                  </div>

                  {upcomingVisit.agent_phone && (
                    <>
                      <span className="h-4 w-px bg-slate-800 hidden sm:inline" />
                      {/* Phone contact */}
                      <a 
                        href={`tel:${upcomingVisit.agent_phone}`}
                        className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-blue-400 transition font-medium"
                      >
                        <Phone className="w-4 h-4 text-slate-450 shrink-0" />
                        <span>{upcomingVisit.agent_phone}</span>
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Vertical Divider line */}
            <div className="w-px bg-slate-800/80 self-stretch hidden md:block" />

            {/* Right actions block */}
            <div className="p-6 md:p-8 flex flex-col justify-center items-center gap-3 shrink-0 bg-slate-900/10 md:w-60">
              <Button asChild className="w-full font-bold gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-blue-500/20 transition duration-150 py-3.5 px-6 rounded-xl"><Link to={role === "agent" ? "/dashboard/agent/visits" : "/dashboard/appointments"}><span>View Details</span><ArrowRight className="w-4 h-4" /></Link></Button>
              
              <Link 
                to={role === "agent" ? "/dashboard/agent/visits" : "/dashboard/appointments"}
                className="inline-flex items-center gap-2 text-xs font-bold text-blue-400 hover:text-blue-300 transition duration-150 hover:underline"
              >
                <Calendar className="w-3.5 h-3.5" />
                <span>{role === "agent" ? "Open Visits Queue" : "Manage Visit"}</span>
              </Link>
            </div>
          </section>
        );
      })()}

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => <KPI key={k.label} loading={loading} {...k} />)}
      </div>

      {(role === "owner" || role === "agent" || role === "office" || role === "admin" || role === "super_admin") && (
        <section className="mt-8 rounded-3xl border border-border bg-background p-6" data-testid="property-pipeline-summary">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-muted-foreground">Property lifecycle</p>
              <h2 className="font-display text-2xl font-black text-primary">Draft → Verification → Published → Sold</h2>
            </div>
            <Button asChild variant="outline" className="gap-2 font-bold"><Link to={role === "admin" || role === "super_admin" ? "/dashboard/admin/approvals" : role === "office" ? "/dashboard/office/verification" : "/dashboard/properties"}>Open pipeline <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {pipeline.map((p) => <StatusTile key={p.status} {...p} />)}
          </div>
        </section>
      )}

      <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="dashboard-action-cards">
        {actions.map((a) => <ActionCard key={a.title} {...a} />)}
      </div>
    </div>
  );
}

function kpisFor(role, stats) {
  const counts = countMap(stats.properties || []);
  const leads = countMap(stats.leads || []);
  const demands = countMap(stats.plot_demands || []);
  if (role === "buyer") return [
    { icon: Heart, label: "Saved", value: stats.saved || 0, to: "/dashboard/saved" },
    { icon: Calendar, label: "Site Visits", value: stats.appointments || 0, to: "/dashboard/appointments" },
    { icon: MapPinned, label: "Plots on Demand", value: total(demands), to: "/dashboard/plot-demands" },
    { icon: Bell, label: "Notifications", value: stats.notifications || 0, to: "/dashboard/notifications" },
  ];
  if (role === "owner") return [
    { icon: Building2, label: "Listings", value: total(counts), to: "/dashboard/properties" },
    { icon: ShieldCheck, label: "Published", value: counts.PUBLISHED || 0, to: "/dashboard/properties?status=PUBLISHED" },
    { icon: Megaphone, label: "Buyer Interest", value: total(leads), to: "/dashboard/properties" },
    { icon: Calendar, label: "Listing Site Visits", value: stats.appointments || 0, to: "/dashboard/properties" },
  ];
  if (role === "agent") return [
    { icon: Building2, label: "Listings", value: total(counts), to: "/dashboard/properties" },
    { icon: ShieldCheck, label: "Published", value: counts.PUBLISHED || 0, to: "/dashboard/properties?status=PUBLISHED" },
    { icon: Megaphone, label: "Leads", value: total(leads), to: "/dashboard/leads" },
    { icon: Calendar, label: "Visits", value: stats.appointments || 0, to: "/dashboard/agent/visits" },
  ];
  if (role === "office") return [
    { icon: ClipboardCheck, label: "Pending", value: counts.PENDING_VERIFICATION || 0, to: "/dashboard/office/verification" },
    { icon: FileCheck2, label: "Verified", value: counts.VERIFIED || 0, to: "/dashboard/office/verification" },
    { icon: Megaphone, label: "CRM Leads", value: total(leads), to: "/dashboard/office/crm-leads" },
    { icon: Calendar, label: "Visits", value: stats.appointments || 0, to: "/dashboard/office/site-visits" },
  ];
  return [
    { icon: Building2, label: "Properties", value: total(counts), to: "/dashboard/admin/approvals" },
    { icon: Users, label: "Users", value: stats.users || 0, to: "/dashboard/admin/users" },
    { icon: BarChart3, label: "Leads", value: total(leads), to: "/dashboard/admin/lead-assignments" },
    { icon: Eye, label: "Events", value: stats.events || 0, to: "/dashboard/admin/system" },
  ];
}

function actionsFor(role) {
  if (role === "buyer") return [
    { title: "Search verified land", desc: "Continue browsing verified land and plot listings.", to: "/properties?category=LAND&verified_documents=true", icon: MapPinned, cta: "Search land" },
    { title: "Open Plots on Demand", desc: "Tell offices and agents what kind of plot you need.", to: "/plot-on-demand", icon: Megaphone, cta: "Create request" },
    { title: "Saved shortlist", desc: "Compare and revisit your saved properties.", to: "/dashboard/saved", icon: Heart, cta: "Open saved" },
  ];
  if (role === "owner") return [
    { title: "Add property", desc: "Use the guided flow with images, documents and a confirmed map pin.", to: "/dashboard/property/new", icon: PlusSquare, cta: "Add property" },
    { title: "Resume listing work", desc: "Complete drafts, correct rejected listings and resubmit them for verification.", to: "/dashboard/properties?status=DRAFT", icon: Layers, cta: "Open listings" },
    { title: "Verification profile", desc: "Review your contact and identity verification before submitting a listing.", to: "/dashboard/profile", icon: ShieldCheck, cta: "Review profile" },
  ];
  if (role === "agent") return [
    { title: "Add property", desc: "Use the 7-step flow with images, documents and map pin.", to: "/dashboard/property/new", icon: PlusSquare, cta: "Add property" },
    { title: "Property status board", desc: "Track draft, pending, verified, published, booked and sold states.", to: "/dashboard/properties", icon: Layers, cta: "View properties" },
    { title: "Leads and follow-ups", desc: "Review buyer contact history and lead status.", to: "/dashboard/leads", icon: Megaphone, cta: "Open leads" },
  ];
  if (role === "office") return [
    { title: "Verify documents", desc: "Review uploaded registry, mutation, khasra and ownership proof.", to: "/dashboard/office/verification", icon: ClipboardCheck, cta: "Open queue" },
    { title: "CRM follow-up", desc: "Track lead journey, assignment and visit scheduling.", to: "/dashboard/office/crm-leads", icon: Megaphone, cta: "Open CRM" },
    { title: "Site visit queue", desc: "Accept, reject and verify site visits.", to: "/dashboard/office/site-visits", icon: Calendar, cta: "Open visits" },
  ];
  return [
    { title: "Add property", desc: "List a new property directly into the inventory with images, documents, and map pin.", to: "/dashboard/property/new?type=land", icon: PlusSquare, cta: "Add property" },
    { title: "Final approval queue", desc: "Publish, reject, feature or audit property listings.", to: "/dashboard/admin/approvals", icon: ShieldCheck, cta: "Review approvals" },
    { title: "Users and offices", desc: "Manage buyers, owners, agents, offices and statuses.", to: "/dashboard/admin/users", icon: Users, cta: "Manage users" },
    { title: "Analytics and performance", desc: "Track views, contacts, WhatsApp clicks and property conversion.", to: "/dashboard/admin/analytics", icon: BarChart3, cta: "Open analytics" },
  ];
}

function KPI({ icon: Icon, label, value, loading, to }) {
  const content = (
    <div className="bg-background border border-border rounded-2xl p-5 shadow-sm hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 cursor-pointer" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary mb-3 shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
        <Icon className="w-5 h-5 group-hover:animate-icon-bob" strokeWidth={1.8}/>
      </span>
      <p className="font-display text-3xl font-black tabular-nums text-primary">{loading ? "…" : (value ?? 0)}</p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-2">{label}</p>
    </div>
  );
  if (to) return <Link to={to} className="group block">{content}</Link>;
  return content;
}

function ActionCard({ title, desc, to, icon: Icon, cta }) {
  return (
    <div className="group bg-background border border-border rounded-2xl p-6 shadow-sm hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg transition" data-testid={`action-${title.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 text-primary mb-4 shadow-sm group-hover:scale-110 group-hover:-rotate-3 group-hover:ring-primary/25 transition-all duration-300 animate-gradient-pan">
        <Icon className="w-6 h-6 group-hover:animate-icon-bob" strokeWidth={1.8}/>
      </span>
      <h3 className="font-display font-bold text-lg text-primary">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2 min-h-[42px]">{desc}</p>
      <Button asChild className="mt-5 font-bold gap-2"><Link to={to}>{cta}<ArrowRight className="w-4 h-4" /></Link></Button>
    </div>
  );
}

function StatusTile({ status, count }) {
  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-4 hover:border-primary/30 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{prettyStatus(status)}</p>
      <p className="mt-2 font-display text-3xl font-black text-primary tabular-nums">{count}</p>
    </div>
  );
}

function countMap(rows = []) {
  const out = {};
  if (Array.isArray(rows)) {
    rows.forEach((r) => { out[String(r.status || r.new_status || "TOTAL").toUpperCase()] = Number(r.count || r.total || 0); });
  } else if (rows && typeof rows === "object") {
    Object.assign(out, rows);
  }
  return out;
}
function total(obj) { return Object.values(obj || {}).reduce((a, b) => a + Number(b || 0), 0); }
function normalizePipeline(rows) {
  const counts = countMap(rows);
  return ["DRAFT", "PENDING_VERIFICATION", "VERIFIED", "PUBLISHED", "BOOKED", "PENDING_SOLD_VERIFICATION", "SOLD"].map((status) => ({ status, count: counts[status] || 0 }));
}
